import { useMemo } from 'react';
import { ROUTES } from '@/lib/constants';
import { useTranslation } from 'react-i18next';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';
import { pnlSegments, SegmentId, SelectOption, sizeSegments } from '@/lib/types/hypertrackerTypes';
import { segmentsLabels } from '@/lib/constants/hyperTrackerConstants';

const useMenuList = ({ member, hasApiToken }) => {
    const { t } = useTranslation('translation');
    const isMemberAuthorized = useIsMemberAuthorized();

    const sizeSegmentsOptions: SelectOption<SegmentId>[] = useMemo(() => {
        return sizeSegments.map((segment) => ({
            label: t(segmentsLabels[segment]),
            value: segment,
        }));
    }, [t]);

    const pnlSegmentsOptions: SelectOption<SegmentId>[] = useMemo(() => {
        return pnlSegments.map((segment) => ({
            label: t(segmentsLabels[segment]),
            value: segment,
        }));
    }, [t]);

    const list = [
        {
            id: 'avatar',
            label: member?.username,
            visible: true,
            authorized: true,
            subMenu: [
                {
                    groupLabel: '',
                    groupData: [
                        {
                            name: t('account_settings'),
                            isExternal: false,
                            url: ROUTES.PROFILE_SETTINGS,
                        },
                        {
                            name: t('go_to_cmm'),
                            isExternal: false,
                            url: '/',
                        },
                        {
                            name: t('pricing'),
                            isExternal: false,
                            url: ROUTES.HYPERTRACKER_PRICING,
                        },
                        {
                            name: t('logout'),
                            isExternal: false,
                            url: 'logout',
                        },
                    ],
                    type: 'avatar',
                },
            ],
            url: null,
            group: 'main',
        },
        {
            id: 'menu_alphafeed',
            label: 'menu_alphafeed',
            url: ROUTES.HYPERTRACKER_ALPHAFEED,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'trade',
        },
        {
            id: 'menu_home',
            label: 'menu_home',
            url: ROUTES.HYPERTRACKER,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'main',
        },
        {
            id: 'menu_perps',
            label: 'menu_perps',
            url: ROUTES.HYPERTRACKER_PERPS,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'market_analytics',
        },
        {
            id: 'menu_radar',
            label: 'menu_radar',
            url: ROUTES.HYPERTRACKER_MARKET_RADAR,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'market_analytics',
        },
        {
            id: 'menu_cohorts',
            label: 'menu_cohorts',
            url: '/hypertracker/segments/:id',
            subMenu: [
                { groupLabel: t('header.size'), groupData: sizeSegmentsOptions, type: 'size' },
                { groupLabel: t('header.pnl'), groupData: pnlSegmentsOptions, type: 'pnl' },
            ],
            visible: true,
            authorized: false,
            group: 'market_analytics',
        },
        {
            id: 'menu_heatmap',
            label: 'menu_heatmap',
            url: ROUTES.HYPERTRACKER_HEATMAP,
            subMenu: [],
            authorized: false,
            visible: true,
            group: 'market_analytics',
        },
        {
            id: 'menu_liquidations',
            label: 'menu_liquidations',
            url: ROUTES.HYPERTRACKER_LIQUIDATIONS,
            subMenu: [],
            authorized: false,
            visible: true,
            group: 'market_analytics',
        },
        {
            id: 'menu_flows',
            label: 'menu_flows',
            url: ROUTES.HYPERTRACKER_FLOWS,
            subMenu: [],
            authorized: false,
            visible: false,
            group: 'market_analytics',
        },
        {
            id: 'menu_builders',
            label: 'menu_builders',
            url: ROUTES.HYPERTRACKER_BUILDER,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'overview',
        },
        {
            id: 'menu_leaderboard',
            label: 'menu_leaderboard',
            url: ROUTES.HYPERTRACKER_LEADERBOARDS,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'overview',
        },
        {
            id: 'menu_hip3',
            label: 'menu_hip3',
            url: ROUTES.HYPERTRACKER_HIP,
            subMenu: [],
            visible: false,
            authorized: false,
            group: 'overview',
        },

        {
            id: 'menu_stats',
            label: 'menu_stats',
            url: ROUTES.HYPERTRACKER_STATS,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'overview',
        },
        {
            id: 'menu_favorites',
            label: 'menu_favorites',
            url: ROUTES.HYPERTRACKER_FAVORITES,
            subMenu: [],
            visible: true,
            authorized: true,
            group: 'tools',
        },
        {
            id: 'menu_alerts',
            label: 'menu_alerts',
            url: ROUTES.HYPERTRACKER_ALERTS,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'tools',
        },
        {
            id: 'menu_cmm_dashboard',
            label: 'menu_cmm_dashboard',
            url: ROUTES.HOME,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'tools',
        },
        {
            id: 'menu_api',
            label: hasApiToken ? 'menu_api_dashboard' : 'menu_api_new',
            url: hasApiToken ? ROUTES.HYPERTRACKER_API_DASHBOARD : ROUTES.HYPERTRACKER_API,
            subMenu: [],
            visible: true,
            authorized: false,
            group: 'api',
        },
    ];

    if (!isMemberAuthorized) {
        return list.filter((item) => {
            if (item.id === 'avatar') return false;
            return item.visible;
        });
    }

    return list.filter((item) => item.visible);
};

export default useMenuList;
